import React from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";

import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import CardContent from "@material-ui/core/CardContent";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import GenericButton from "components/Buttons/GenericButton";

// @material-ui/icons components
import { errorMessages, titles, labels, messages } from "resources/resources";
import { CONFIRMATION_ACTIONS, REGISTRATION_CONSTS } from "utils/const";

import componentStyles from "assets/theme/components/navbars/auth-navbar.js";
import { FormControlLabel, Grid, OutlinedInput, Radio, RadioGroup } from "@material-ui/core";
import CustomDialogTitle from "components/Dialogs/CustomDialogTitle";

const useStyles = makeStyles(componentStyles);

function RecoverModal(props) {
  const classes = { ...useStyles() };
  const theme = useTheme();
  const intl = useIntl();
  const [nifError, setNifError] = React.useState("");

  const SUBJECT_TYPES = {
    CITIZEN: "citizen",
    COMPANY: "company",
  };

  const getSubjectType = () => props.state?.recoverPasswordSubjectType || SUBJECT_TYPES.CITIZEN;
  const isCompany = getSubjectType() === SUBJECT_TYPES.COMPANY;

  const getNifError = (value) => {
    if (!value) return intl.formatMessage(errorMessages.ErrorMessage_FieldRequired);
    if (value.length > REGISTRATION_CONSTS.MAX_NIF)
      return intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
    return "";
  };

  const handleNifChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "");
    props.onChange("recoverPasswordNif", digitsOnly);
    if (nifError) {
      setNifError(getNifError(digitsOnly));
    }
  };

  React.useEffect(() => {
    if (!isCompany && nifError) {
      setNifError("");
    }
  }, [isCompany, nifError]);

  const handleSubmit = () => {
    const normalizedNif = (props.state?.recoverPasswordNif || "").replace(/\D/g, "");
    if (isCompany) {
      const nextError = getNifError(normalizedNif);
      if (nextError) {
        setNifError(nextError);
        return;
      }
    }
    setNifError("");
    props.handleCloseRecover();
    props.showAuthenticationModal(
      true,
      isCompany ? normalizedNif : props.state.recoverPasswordPhone,
      CONFIRMATION_ACTIONS.RECOVER_PASSWORD,
      {
        subjectType: getSubjectType(),
      }
    );
  };

  return (
    <Dialog
      open={props.recoverModal}
      onClose={props.handleCloseRecover}
      classes={{ paper: classes.modal }}
    >
      <CardContent classes={{ root: classes.cardContent }}>
        <Grid container>
          <Grid item>
            <Box
              color={theme.palette.gray[600]}
              textAlign="center"
              marginBottom="1rem"
              marginTop="0px"
              fontSize="2rem"
            >
              <Box
                style={{
                  fontSize: "2rem",
                  lineHeight: "3rem",
                  color: theme.palette.primary.greyBlue,
                }}
                className={classes.modalHeader}
              >
                {intl.formatMessage(titles.Title_Auth_Recover_Password_Modal)}
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <CustomDialogTitle
              id="customized-dialog-title"
              onClose={props.handleCloseRecover}
            />
          </Grid>
        </Grid>
        <Box marginLeft="-27px">
          <Box
            style={{
              marginBottom: "1.5rem",
              marginTop: ".5rem",
              fontSize: "1rem",
              color: theme.palette.gray[900],
              textAlign: "left",
            }}
            className={classes.modalRecoverHeader1}
          >
            {intl.formatMessage(messages.Message_Auth_Recover_Password_Modal_1)}
          </Box>
          <Box
            style={{
              marginBottom: "1.5rem",
              marginTop: ".5rem",
              fontSize: "1rem",
              color: theme.palette.gray[900],
              textAlign: "left",
            }}
            className={classes.modalRecoverHeader2}
          >
            {intl.formatMessage(messages.Message_Auth_Recover_Password_Modal_2)}
          </Box>
        </Box>
        <form>
          <FormControl className={classes.recoverFormControl} component="fieldset" style={{ marginTop: "15px" }}>
            <FormLabel className={classes.authenticationLabels} id="recover-password-subject-type-label">
              {intl.formatMessage(labels.Label_Auth_Recover_Password_SubjectType)}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="recover-password-subject-type-label"
              name="recoverPasswordSubjectType"
              value={getSubjectType()}
              onChange={(event) => props.onChange("recoverPasswordSubjectType", event.target.value)}
            >
              <FormControlLabel
                value={SUBJECT_TYPES.CITIZEN}
                control={<Radio color="primary" />}
                label={intl.formatMessage(labels.Label_Auth_Register_TableHead_EntityTypeIndividual)}
              />
              <FormControlLabel
                value={SUBJECT_TYPES.COMPANY}
                control={<Radio color="primary" />}
                label={intl.formatMessage(labels.Label_Auth_Register_TableHead_EntityTypeCompany)}
              />
            </RadioGroup>
          </FormControl>
          {isCompany && (
            <FormControl className={classes.recoverFormControlCompact}>
              <FormLabel className={classes.authenticationLabels}>
                {intl.formatMessage(labels.Label_Auth_Recover_Password_NIF)}
              </FormLabel>
              <OutlinedInput
                placeholder={intl.formatMessage(titles.Label_RecoverPasswordModal_NifInputPlaceholder)}
                autoComplete="off"
                type="text"
                inputProps={{ inputMode: "numeric", maxLength: REGISTRATION_CONSTS.MAX_NIF }}
                value={props.state?.recoverPasswordNif || ""}
                onChange={handleNifChange}
              />
              {nifError && (
                <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                  {nifError}
                </FormHelperText>
              )}
            </FormControl>
          )}
          {!isCompany && (
            <FormControl className={classes.recoverFormControlCompact}>
              <FormLabel className={classes.authenticationLabels}>
                {intl.formatMessage(labels.Label_Auth_Recover_Password_MobilePhone)}
              </FormLabel>
              <OutlinedInput
                placeholder={intl.formatMessage(
                  titles.Label_RecoverPasswordModal_MobilePhoneInputPlaceholder
                )}
                autoComplete="off"
                type="text"
                onChange={(e) => props.onChange("recoverPasswordPhone", e.target.value)}
              />
            </FormControl>
          )}
          <Box
            textAlign="right"
            marginTop="1.25rem"
            marginBottom="1rem"
            padding="5px"
          >
            <GenericButton
              typeSubmit={false}
              color="tertiary"
              onClick={props.handleCloseRecover}
            >
              Cancelar
            </GenericButton>
            <GenericButton
              typeSubmit={false}
              color="primary"
              onClick={handleSubmit}
              style={{ marginLeft: "15px" }}
              disabled={props.authState.login_loading}
              loading={props.authState.login_loading}
            >
              Ok
            </GenericButton>
          </Box>
        </form>
      </CardContent>
    </Dialog>
  );
}
const mapStateToProps = (state) => ({ ...state });

export default connect(mapStateToProps)(RecoverModal);
